import { atom } from "jotai"
import { atomWithStorage } from "jotai/utils"
import { isEqual } from "lodash"

import type { CartItem } from "types/types"

export const cartAtom = atomWithStorage<CartItem[]>("cart", [])

export const cartTotalAtom = atom<number>(0)

export const clearCartAtom = atom(null, (_, set) => {
  set(cartAtom, [])
})

export const addToCartAtom = atom(
  null,
  (
    get,
    set,
    {
      id,
      productId,
      productTitle,
      productType,
      variantId,
      variantTitle,
      addons,
      quantity,
      installDate,
      crossSells,
      crossSellUuid
    }: CartItem
  ) => {
    const cart = get(cartAtom)
    const itemInCart = cart.find(item => {
      return (
        // item.id === id
        // Add to cart by comparing rather than the id as the id is generated in the client - need to compare the rest of the data to see if its the same item
        item.productId === productId &&
        item.variantId === variantId &&
        isEqual(item.addons, addons) &&
        item.installDate === installDate &&
        isEqual(item.crossSells, crossSells) &&
        item.crossSellUuid === crossSellUuid
      )
    })
    if (itemInCart) {
      const newCart = cart.map(item => {
        if (item.id === itemInCart.id) {
          return {
            ...item,
            quantity: item.quantity + quantity
          }
        }
        return item
      })
      set(cartAtom, newCart)
    } else {
      set(cartAtom, [
        ...cart,
        {
          id,
          productId,
          productTitle,
          productType,
          variantId,
          variantTitle,
          addons,
          installDate,
          crossSells,
          quantity,
          crossSellUuid
        }
      ])
    }
  }
)

export const removeFromCartAtom = atom(null, (get, set, cartItem: CartItem) => {
  const cart = get(cartAtom)
  let newCart: CartItem[] = []
  // when removing a cart item, if it has a crossSellUuid, run through the cart array and it's crossSells array and remove any crossSells with a uuid that matches the cartItem's crossSellUuid (shouldn't happen as the remove action is not shown for cartItems with a crossSellUuid)
  if (cartItem.crossSellUuid) {
    const cartWithCrossSellsRemoved = cart.map(item => {
      if (item.crossSells) {
        return {
          ...item,
          crossSells: item.crossSells.filter(
            crossSell => crossSell.uuid !== cartItem.crossSellUuid
          )
        }
      }
      return item
    })
    newCart = cartWithCrossSellsRemoved.filter(item => item.id !== cartItem.id)
  }
  // if cartItem has crossSells, remove them from the cart by looping through the cart and getting the cart item to remove by matching the crossSellUuid
  else if (cartItem.crossSells && cartItem.crossSells.length > 0) {
    const otherItemsToRemoveFromCartAlongWithThisOne = cartItem.crossSells.map(
      crossSell => cart.find(item => item.crossSellUuid === crossSell.uuid)
    )
    newCart = cart.filter(
      item =>
        item.id !== cartItem.id &&
        !otherItemsToRemoveFromCartAlongWithThisOne.includes(item)
    )
  }
  // otherwise just remove the cartItem
  else {
    newCart = cart.filter(item => item.id !== cartItem.id)
  }
  set(cartAtom, newCart)
})

export const setCartItemQuantityAtom = atom(
  null,
  (
    get,
    set,
    { cartItem, quantity }: { cartItem: CartItem; quantity: number }
  ) => {
    if (quantity < 1) return
    const cart = get(cartAtom)
    const itemInCart = cart.find(item => {
      return (
        item.id === cartItem.id
        // item.productId === cartItem.productId &&
        // item.variantId === cartItem.variantId &&
        // isEqual(item.addons, cartItem.addons) &&
        // item.installDate === cartItem.installDate &&
        // isEqual(item.crossSells, cartItem.crossSells)
      )
    })
    if (itemInCart) {
      itemInCart.quantity = quantity
    }
    set(cartAtom, [...cart])
  }
)

export const googleMapsLoadedAtom = atom(false)
